import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
    { href: "#about", label: "About" },
    { href: "#services", label: "Services" },
    { href: "#markets", label: "Markets" },
    { href: "#supply-chain", label: "Network" },
    { href: "#vision", label: "Vision" },
    { href: "#contact", label: "Contact" },
];

export const Nav = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <motion.header
            data-testid="site-nav"
            initial={{ y: -40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            className={`fixed top-0 left-0 right-0 z-40 transition-colors duration-300 ${
                scrolled
                    ? "bg-[#0B0807]/85 backdrop-blur-xl border-b border-white/5"
                    : "bg-transparent"
            }`}
        >
            <div className="flex items-center justify-between px-4 sm:px-8 lg:px-12 py-4">
                <a
                    href="#hero"
                    data-testid="nav-logo"
                    className="flex items-center gap-3 group"
                >
                    <img
                        src="/duha-monogram.png"
                        alt="DUHA"
                        className="h-9 sm:h-10 w-auto group-hover:scale-105 transition-transform"
                    />
                    <span className="font-display text-lg sm:text-xl font-bold tracking-tight text-[#F5EFE6]">
                        DUHA<span className="text-[#D69331]">.</span>
                    </span>
                    <span className="hidden sm:inline font-mono-text text-[10px] uppercase tracking-[0.25em] text-[#8B7A6F] ml-1 border-l border-white/10 pl-3">
                        Global Trading
                    </span>
                </a>

                <nav className="hidden lg:flex items-center gap-8">
                    {LINKS.map((l) => (
                        <a
                            key={l.href}
                            href={l.href}
                            data-testid={`nav-link-${l.label.toLowerCase()}`}
                            className="font-mono-text text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors"
                        >
                            {l.label}
                        </a>
                    ))}
                </nav>

                <a
                    href="mailto:info@duhagt.com"
                    data-testid="nav-cta-email"
                    className="hidden lg:inline-flex items-center gap-2 px-5 py-2.5 bg-[#D69331] text-black font-mono-text text-xs font-bold uppercase tracking-[0.2em] hover:bg-white transition-colors"
                >
                    Let's Talk
                    <span aria-hidden>→</span>
                </a>

                <button
                    data-testid="nav-toggle-mobile"
                    onClick={() => setOpen((v) => !v)}
                    className="lg:hidden p-2 text-white"
                    aria-label="Toggle menu"
                >
                    {open ? <X size={22} /> : <Menu size={22} />}
                </button>
            </div>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="lg:hidden overflow-hidden bg-[#0B0807] border-t border-white/5"
                    >
                        <div className="px-4 py-6 flex flex-col gap-4">
                            {LINKS.map((l) => (
                                <a
                                    key={l.href}
                                    href={l.href}
                                    data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                                    onClick={() => setOpen(false)}
                                    className="font-display text-2xl uppercase text-white hover:text-[#D69331] transition-colors"
                                >
                                    {l.label}
                                </a>
                            ))}
                            <a
                                href="mailto:info@duhagt.com"
                                data-testid="nav-mobile-cta"
                                className="mt-2 inline-flex items-center justify-center px-5 py-3 bg-[#D69331] text-black font-mono-text text-xs font-bold uppercase tracking-[0.2em]"
                            >
                                info@duhagt.com
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.header>
    );
};
